import {
	type HomeownerGoal,
	type HomeownerPropertyType,
	isPincode,
	type OnboardingPrefill,
	type PropertyStatus,
} from "@shared/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SingleImageUpload } from "@/components/ui/image-upload";
import { StepProgress } from "@/components/ui/step-progress";
import { SuccessScreen } from "@/components/ui/success-screen";
import {
	createHomeownerProfile,
	createProperty,
} from "@/features/onboarding/services/onboarding-api.service";
import {
	FieldInput,
	FieldSelect,
	FloatPanel,
	StudioButton,
} from "@/features/studio/theme";
import {
	keepOrFill,
	useApplyPrefillOnce,
} from "../hooks/use-onboarding-prefill";
import {
	goalOptions,
	homeownerPropertyTypeOptions,
	propertyStatusOptions,
} from "../options";
import { BackLink } from "./back-link";
import { FieldGroupLabel, OptionChips, PincodeInput } from "./fields";

type Step = "goals" | "profile" | "property" | "complete";
const STEPS: Step[] = ["goals", "profile", "property", "complete"];

export interface HomeownerOnboardingFlowProps {
	/** Back from the first step. Without it the first step has no Back link. */
	onExit?: () => void;
	/**
	 * Rendered in place of the Back link on the first step only, so a later
	 * step never shows it beside that step's own Back.
	 */
	exitControl?: React.ReactNode;
	/** Details the account already gave; fills empty fields once. */
	prefill?: OnboardingPrefill;
	/**
	 * The profile was saved. This occurs on the property step, before the
	 * property is sent or when the user skips it.
	 */
	onProfileCreated: () => void;
	/** The last screen's button. */
	onFinished: () => void;
}

/**
 * Homeowner onboarding: goals, then profile, then an optional property.
 * Navigation is the caller's, so the same flow can be mounted outside the
 * onboarding route.
 *
 * The profile step only checks its fields. The flow saves the profile on the
 * property step, with the property or on Skip. Until then the profile row
 * keeps the placeholder name. For an account with no other finished role,
 * `setUserType` (auth.service.ts) then still lets the role picker change the
 * role (#483). After the save the server refuses that change, so the flow
 * hides its Back link.
 */
export function HomeownerOnboardingFlow({
	onExit,
	exitControl,
	prefill,
	onProfileCreated,
	onFinished,
}: HomeownerOnboardingFlowProps) {
	const { t } = useTranslation();
	const [step, setStep] = useState<Step>("goals");
	const [isLoading, setIsLoading] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [profileSaved, setProfileSaved] = useState(false);

	const [goals, setGoals] = useState<HomeownerGoal[]>([]);

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [pincode, setPincode] = useState("");
	const [photoUrl, setPhotoUrl] = useState("");

	useApplyPrefillOnce(prefill, (known) => {
		setName((current) => keepOrFill(current, known.name));
		setEmail((current) => keepOrFill(current, known.email));
		setPincode((current) => keepOrFill(current, known.pincode));
		setPhotoUrl((current) => keepOrFill(current, known.profilePictureUrl));
	});

	const [propertyType, setPropertyType] =
		useState<HomeownerPropertyType>("apartment");
	const [propertyTypeOther, setPropertyTypeOther] = useState("");
	const [propertyStatus, setPropertyStatus] = useState<PropertyStatus | null>(
		null,
	);
	const [sizeSqft, setSizeSqft] = useState("");
	const [bedrooms, setBedrooms] = useState("2");
	const [bathrooms, setBathrooms] = useState("2");

	const stepNames: Record<Step, string> = {
		goals: t("onboarding.homeowner.steps.goals"),
		profile: t("onboarding.homeowner.steps.profile"),
		property: t("onboarding.homeowner.steps.property"),
		complete: t("onboarding.homeowner.steps.complete"),
	};

	const handleBack = () => {
		if (step === "goals") onExit?.();
		else if (step === "profile") setStep("goals");
		else if (step === "property") setStep("profile");
	};

	const handleGoalsSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (goals.length === 0) {
			toast.error(t("onboarding.homeowner.selectAtLeastOneGoal"));
			return;
		}
		setStep("profile");
	};

	const handleProfileSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		if (!name.trim()) {
			toast.error(t("onboarding.homeowner.pleaseEnterName"));
			return;
		}
		if (pincode && !isPincode(pincode)) {
			toast.error(t("onboarding.fields.pincodeInvalid"));
			return;
		}
		setStep("property");
	};

	// Saves the profile. After one save that succeeds, it does not send it
	// again. If the save fails, it shows the error, goes back to the profile
	// step and returns false.
	const saveProfile = async (): Promise<boolean> => {
		if (profileSaved) return true;
		const result = await createHomeownerProfile({
			name: name.trim(),
			email: email.trim() || undefined,
			profilePictureUrl: photoUrl || undefined,
			pincode: pincode || null,
			goals,
		});
		if (!result.success) {
			toast.error(
				result.error || t("onboarding.homeowner.failedToCreateProfile"),
			);
			setStep("profile");
			return false;
		}
		setProfileSaved(true);
		onProfileCreated();
		toast.success(t("onboarding.homeowner.profileCreated"));
		return true;
	};

	const handleSkip = async () => {
		setIsLoading(true);
		try {
			if (await saveProfile()) setStep("complete");
		} catch {
			toast.error(t("common.somethingWentWrong"));
		} finally {
			setIsLoading(false);
		}
	};

	const handlePropertySubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		setIsLoading(true);
		try {
			if (!(await saveProfile())) return;
			const result = await createProperty({
				type: propertyType,
				typeOther:
					propertyType === "other" ? propertyTypeOther.trim() || null : null,
				status: propertyStatus,
				sizeSqft: sizeSqft ? Number.parseInt(sizeSqft, 10) : undefined,
				rooms: {
					bedrooms: Number.parseInt(bedrooms, 10),
					bathrooms: Number.parseInt(bathrooms, 10),
					livingAreas: 1,
				},
			});

			if (result.success) {
				toast.success(t("onboarding.homeowner.propertyAdded"));
				setStep("complete");
			} else {
				toast.error(
					result.error || t("onboarding.homeowner.failedToAddProperty"),
				);
			}
		} catch {
			toast.error(t("common.somethingWentWrong"));
		} finally {
			setIsLoading(false);
		}
	};

	if (step === "complete") {
		return (
			<>
				<StepProgress
					steps={STEPS.map((s) => stepNames[s])}
					currentStep={stepNames.complete}
					className="mb-8"
				/>
				<SuccessScreen
					title={t("onboarding.homeowner.completeTitle")}
					description={t("onboarding.homeowner.completeSubtitle")}
					actionLabel={t("common.goToDashboard")}
					onAction={onFinished}
				/>
			</>
		);
	}

	return (
		<>
			{step === "goals" && exitControl}
			{(step !== "goals" || onExit) && !profileSaved && (
				<BackLink onClick={handleBack} />
			)}

			<StepProgress
				steps={STEPS.map((s) => stepNames[s])}
				currentStep={stepNames[step]}
				className="mb-8"
			/>

			{step === "goals" && (
				<FloatPanel
					elevation={2}
					padding="clamp(24px, 6vw, 40px) clamp(20px, 5vw, 32px)"
				>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.homeowner.goalsStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.homeowner.goalsStepSubtitle")}
					</p>
					<form onSubmit={handleGoalsSubmit} className="space-y-6">
						<OptionChips
							label={t("onboarding.homeowner.goalsStepTitle")}
							options={goalOptions(t)}
							selected={goals}
							onChange={(next) => setGoals(next as HomeownerGoal[])}
							gridClassName="grid grid-cols-1 gap-2 sm:grid-cols-2"
							hideLabel
						/>
						<StudioButton
							type="submit"
							variant="primary"
							style={{ width: "100%" }}
						>
							{t("common.continue")}
						</StudioButton>
					</form>
				</FloatPanel>
			)}

			{step === "profile" && (
				<FloatPanel
					elevation={2}
					padding="clamp(24px, 6vw, 40px) clamp(20px, 5vw, 32px)"
				>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.homeowner.profileStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.homeowner.profileStepSubtitle")}
					</p>

					<form onSubmit={handleProfileSubmit} className="space-y-5">
						<div>
							<FieldGroupLabel>
								{t("onboarding.fields.profilePhoto")}
							</FieldGroupLabel>
							<SingleImageUpload
								label=""
								currentUrl={photoUrl || undefined}
								onUpload={setPhotoUrl}
								onRemove={() => setPhotoUrl("")}
								onUploadingChange={setIsUploading}
							/>
						</div>

						<FieldInput
							label={t("onboarding.homeowner.fullName")}
							type="text"
							value={name}
							autoComplete="name"
							onChange={(event) => setName(event.target.value)}
							placeholder={t("onboarding.homeowner.fullNamePlaceholder")}
							required
						/>

						<FieldInput
							label={t("onboarding.homeowner.emailOptional")}
							type="email"
							value={email}
							autoComplete="email"
							onChange={(event) => setEmail(event.target.value)}
							placeholder={t("onboarding.homeowner.emailPlaceholder")}
						/>

						<PincodeInput
							value={pincode}
							onChange={setPincode}
							hint={t("onboarding.homeowner.pincodeHint")}
						/>

						<StudioButton
							type="submit"
							variant="primary"
							disabled={isUploading}
							style={{ width: "100%", marginTop: 24 }}
						>
							{t("common.continue")}
						</StudioButton>
					</form>
				</FloatPanel>
			)}

			{step === "property" && (
				<FloatPanel
					elevation={2}
					padding="clamp(24px, 6vw, 40px) clamp(20px, 5vw, 32px)"
				>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.homeowner.propertyStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.homeowner.propertyStepSubtitle")}
					</p>

					<form onSubmit={handlePropertySubmit} className="space-y-6">
						<OptionChips
							label={t("onboarding.homeowner.propertyType")}
							options={homeownerPropertyTypeOptions(t)}
							selected={[propertyType]}
							onChange={(next) => {
								// A property always has a type: a click on the chosen chip keeps it.
								if (next.length > 0) {
									setPropertyType(next[0] as HomeownerPropertyType);
								}
							}}
							single
							otherValue="other"
							otherText={propertyTypeOther}
							onOtherTextChange={setPropertyTypeOther}
							otherLabel={t("onboarding.homeowner.otherPropertyType")}
						/>

						<OptionChips
							label={t("onboarding.homeowner.propertyStatus")}
							options={propertyStatusOptions(t)}
							selected={propertyStatus ? [propertyStatus] : []}
							onChange={(next) =>
								setPropertyStatus((next[0] as PropertyStatus) ?? null)
							}
							single
						/>

						<FieldInput
							label={t("onboarding.homeowner.sizeSqft")}
							type="number"
							min={0}
							inputMode="numeric"
							value={sizeSqft}
							onChange={(event) => setSizeSqft(event.target.value)}
							placeholder={t("onboarding.homeowner.sizePlaceholder")}
						/>

						<div className="grid grid-cols-2 gap-4">
							<FieldSelect
								label={t("onboarding.homeowner.bedrooms")}
								value={bedrooms}
								onChange={(event) => setBedrooms(event.target.value)}
								options={[1, 2, 3, 4, 5].map((n) => ({
									value: String(n),
									label: `${n} ${n === 1 ? t("onboarding.homeowner.bedroom") : t("onboarding.homeowner.bedrooms")}`,
								}))}
							/>
							<FieldSelect
								label={t("onboarding.homeowner.bathrooms")}
								value={bathrooms}
								onChange={(event) => setBathrooms(event.target.value)}
								options={[1, 2, 3, 4, 5].map((n) => ({
									value: String(n),
									label: `${n} ${n === 1 ? t("onboarding.homeowner.bathroom") : t("onboarding.homeowner.bathrooms")}`,
								}))}
							/>
						</div>

						<div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
							<StudioButton
								type="button"
								variant="secondary"
								disabled={isLoading}
								onClick={() => void handleSkip()}
								className="w-full sm:w-auto sm:flex-1"
							>
								{t("common.skipForNow")}
							</StudioButton>
							<StudioButton
								type="submit"
								variant="primary"
								disabled={isLoading}
								className="w-full sm:w-auto sm:flex-1"
							>
								{isLoading
									? t("common.adding")
									: t("onboarding.homeowner.addProperty")}
							</StudioButton>
						</div>
					</form>
				</FloatPanel>
			)}
		</>
	);
}
